import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import { api } from '../../utils/api';
import { API_ENDPOINTS } from '../../config';
import { openDodoCheckout } from '../../utils/dodo';
import { track } from '../../utils/analytics';
import { showToast } from '../../utils/toastManager';
import { SubscriptionPlan, UserSubscription } from '../../types/subscription';
import '../../styles/upsell-modal.css';

const FREE_PLAN_CODES = ['FREE_PLAN', 'TRIAL_PLAN'];

const EXCLUDED_PATHS = [
  '/sign-in',
  '/sign-up',
  '/restore',
  '/banned',
  '/settings/plan/purchase',
  '/payment',
  '/admin',
  '/resume-builder',
  '/resume-review',
  '/track/board',
];

const CLOSE_DELAY_MS = 30 * 60 * 1000;
const CLOSE_BTN_DELAY_S = 0;

const PLAN_FEATURES: Record<string, string[]> = {
  STARTER: [
    'Unlimited job applications',
    'Kanban pipeline board',
    'AI resume tailoring assistant',
    'Contact & recruiter tracking',
    'Job description archiving',
  ],
  PLUS: [
    'Everything in Starter',
    'Full pipeline analytics',
    'Smart follow-up drafts',
    'Interview prep with AI',
    'Priority support',
  ],
  PREMIUM: [
    'Everything in Plus',
    'Unlimited AI resume versions',
    'Salary negotiation templates',
    'Early access to new features',
    'Dedicated onboarding',
  ],
  PRO: [
    'Unlimited job applications',
    'AI resume tailoring assistant',
    'Smart follow-up drafts',
    'Contact & recruiter tracking',
    'Full pipeline analytics',
    'Job description archiving',
    'Kanban pipeline board',
  ],
  INTENSIVE: [
    'Everything in Pro',
    'Unlimited AI resume versions',
    'Interview prep with AI',
    'Salary negotiation templates',
    'Priority support',
    'Early access to new features',
  ],
};

const getPlanFeatures = (plan: SubscriptionPlan): string[] => {
  const code = plan.code.toUpperCase();
  for (const key of Object.keys(PLAN_FEATURES)) {
    if (code.includes(key)) return PLAN_FEATURES[key];
  }
  const fmt = (n: number) => (n === -1 ? 'Unlimited' : n.toLocaleString());
  return [
    `${fmt(plan.job_position_limit)} job applications`,
    `${fmt(plan.contacts_limit)} contacts`,
    `${fmt(plan.resumes_limit)} resumes`,
    `${fmt(plan.is_ai_tokens_limit ? plan.subscription_ai_tokens : plan.ai_tools_calls_limit)} AI ${plan.is_ai_tokens_limit ? 'tokens' : 'generations'}`,
  ];
};

const isFeatured = (plan: SubscriptionPlan) => {
  const c = plan.code.toUpperCase();
  return c.includes('PLUS') || c.includes('PRO');
};

const hasFreeTrial = (plan: SubscriptionPlan) => {
  const c = plan.code.toUpperCase();
  return c.includes('PLUS') || c.includes('PRO');
};

const periodLabel = (days: number): string => {
  if (days === 90) return '3 months';
  if (days === 180) return '6 months';
  if (days === 365) return 'Annual';
  return `${days}d`;
};

const monthlyEquiv = (price: number | string, days: number): number =>
  Math.round(Number(price) / (days / 30));

const CheckIcon: React.FC<{ featured: boolean }> = ({ featured }) => (
  <span className={`pp-check ${featured ? 'pp-check--featured' : ''}`}>
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
      <path d="M1.5 5L4 7.5L8.5 2.5" stroke={featured ? '#fff' : '#059669'} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </span>
);

const localStorageKey = (uid: string) => `upsell_popup_closed_at:${uid}`;

const shouldShowPopup = (uid: string): boolean => {
  const raw = localStorage.getItem(localStorageKey(uid));
  if (!raw) return true;
  const lastClosed = parseInt(raw, 10);
  if (isNaN(lastClosed)) return true;
  return Date.now() - lastClosed >= CLOSE_DELAY_MS;
};

const UpsellModal: React.FC = () => {
  const location = useLocation();
  const { userProfile } = useUser();

  const [visible, setVisible] = useState(false);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState<number | null>(null);
  const [processingPlan, setProcessingPlan] = useState<string | null>(null);
  const [closeBtnCountdown, setCloseBtnCountdown] = useState(CLOSE_BTN_DELAY_S);
  const [closeBtnVisible, setCloseBtnVisible] = useState(false);

  const userUid = useMemo(() => localStorage.getItem('user_uid'), []);

  const isExcluded = useMemo(() => {
    const path = location.pathname;
    return EXCLUDED_PATHS.some(excluded => path.startsWith(excluded));
  }, [location.pathname]);

  useEffect(() => {
    const handler = () => {
      if (userUid) localStorage.removeItem(localStorageKey(userUid));
      setVisible(true);
    };
    window.addEventListener('show-upsell-modal', handler);
    return () => window.removeEventListener('show-upsell-modal', handler);
  }, [userUid]);

  useEffect(() => {
    if (!userUid || isExcluded || !userProfile) return;

    const check = async () => {
      try {
        const data = await api.get<UserSubscription>(API_ENDPOINTS.subscriptions.getUserSubscription);
        const planCode = data?.plan?.code ?? data?.plan_code ?? '';
        if (!FREE_PLAN_CODES.includes(planCode)) return;
      } catch {
      }

      try {
        const me = await api.get<{
          has_built_resume?: boolean;
          has_tracked_job_applications?: boolean;
        }>(API_ENDPOINTS.me);
        if (!me?.has_built_resume && !me?.has_tracked_job_applications) return;
      } catch {
        return;
      }

      if (!shouldShowPopup(userUid)) return;
      setVisible(true);
    };

    check();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userUid, isExcluded, userProfile]);

  useEffect(() => {
    if (!visible) return;
    track('upsell_popup_shown');

    const fetchPlans = async () => {
      try {
        setLoadingPlans(true);
        const data = await api.get<SubscriptionPlan[]>(API_ENDPOINTS.subscriptions.getPlans);
        if (!data) return;
        setPlans(data.filter(p => p.code !== 'FREE_PLAN' && p.code !== 'TRIAL_PLAN'));
      } catch {
      } finally {
        setLoadingPlans(false);
      }
    };
    fetchPlans();
  }, [visible]);

  useEffect(() => {
    if (!visible) return;

    if (CLOSE_BTN_DELAY_S <= 0) {
      setCloseBtnCountdown(0);
      setCloseBtnVisible(true);
      return;
    }

    setCloseBtnCountdown(CLOSE_BTN_DELAY_S);
    setCloseBtnVisible(false);

    const interval = setInterval(() => {
      setCloseBtnCountdown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          setCloseBtnVisible(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [visible]);

  const availablePeriods = useMemo<number[]>(() => {
    const set = new Set(plans.map(p => p.billing_period_days));
    return Array.from(set).sort((a, b) => a - b);
  }, [plans]);

  useEffect(() => {
    if (availablePeriods.length > 0 && selectedPeriod === null) {
      setSelectedPeriod(availablePeriods[0]);
    }
  }, [availablePeriods, selectedPeriod]);

  const baseline3m = useMemo<Map<string, number>>(() => {
    const map = new Map<string, number>();
    plans
      .filter(p => p.billing_period_days === 90)
      .forEach(p => {
        const tierKey = p.code.replace(/_3_MONTH$|_6_MONTH$|_12_MONTH$|_ANNUAL$/, '');
        map.set(tierKey, monthlyEquiv(p.price, 90));
      });
    return map;
  }, [plans]);

  const visiblePlans = useMemo<SubscriptionPlan[]>(() => {
    if (selectedPeriod === null) return [];
    return plans.filter(p => p.billing_period_days === selectedPeriod);
  }, [plans, selectedPeriod]);

  const getSaving = useCallback((plan: SubscriptionPlan): number => {
    if (plan.billing_period_days === 90) return 0;
    const tierKey = plan.code.replace(/_3_MONTH$|_6_MONTH$|_12_MONTH$|_ANNUAL$/, '');
    const base = baseline3m.get(tierKey);
    if (!base || base === 0) return 0;
    const monthly = monthlyEquiv(plan.price, plan.billing_period_days);
    return Math.round((1 - monthly / base) * 100);
  }, [baseline3m]);

  const periodSaving = useMemo<Map<number, number>>(() => {
    const map = new Map<number, number>();
    availablePeriods.forEach(days => {
      if (days === 90) { map.set(days, 0); return; }
      const savings = plans
        .filter(p => p.billing_period_days === days)
        .map(p => getSaving(p));
      map.set(days, savings.length > 0 ? Math.max(...savings) : 0);
    });
    return map;
  }, [availablePeriods, plans, getSaving]);

  const handleClose = useCallback(() => {
    if (!closeBtnVisible) return;
    if (userUid) {
      localStorage.setItem(localStorageKey(userUid), String(Date.now()));
    }
    track('upsell_popup_closed');
    setVisible(false);
  }, [closeBtnVisible, userUid]);

  const handleSelectPlan = async (plan: SubscriptionPlan) => {
    if (!userProfile?.email) return;
    if (!plan.dodo_price_id) {
      showToast('This plan is not available for purchase yet.', 'error');
      return;
    }
    try {
      setProcessingPlan(plan.code);
      track('upsell_checkout_started', { plan: plan.code, price: Number(plan.price), currency: 'USD' });
      await openDodoCheckout(plan.code);
    } catch (err: any) {
      showToast(err.message || 'Failed to open checkout. Please try again.', 'error');
      setProcessingPlan(null);
    }
  };

  if (!visible) return null;

  return (
    <div className="um-overlay">
      <div className="um-modal">
        <div className="um-close-wrap">
          {closeBtnVisible ? (
            <button className="um-close-btn" onClick={handleClose} aria-label="Close">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M2 2L14 14M14 2L2 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          ) : (
            <div className="um-close-countdown">{closeBtnCountdown}</div>
          )}
        </div>

        <div className="um-header">
          <div className="um-header__eyebrow">Unlock your full potential</div>
          <h2 className="um-header__title">Upgrade to a paid plan</h2>
          <p className="um-header__sub">
            Get unlimited applications, AI tools, analytics, and more.
          </p>
        </div>

        {loadingPlans ? (
          <div className="um-loading">Loading plans…</div>
        ) : (
          <>
            {availablePeriods.length > 1 && (
              <div className="pp-period-toggle um-period-toggle">
                {availablePeriods.map(days => {
                  const saving = periodSaving.get(days) ?? 0;
                  return (
                    <button
                      key={days}
                      className={`pp-period-btn ${selectedPeriod === days ? 'pp-period-btn--active' : ''}`}
                      onClick={() => setSelectedPeriod(days)}
                    >
                      {periodLabel(days)}
                      {saving > 0 && (
                        <span className="pp-period-save">Save {saving}%</span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {visiblePlans.length === 0 ? (
              <div className="um-loading">No plans available at the moment.</div>
            ) : (
              <div className="pp-grid um-grid" style={{ gridTemplateColumns: `repeat(${Math.min(visiblePlans.length, 3)}, 1fr)` }}>
                {visiblePlans.map((plan) => {
                  const featured = isFeatured(plan);
                  const trial = hasFreeTrial(plan);
                  const features = getPlanFeatures(plan);
                  const busy = processingPlan === plan.code;
                  const monthly = monthlyEquiv(plan.price, plan.billing_period_days);
                  const saving = getSaving(plan);

                  return (
                    <div key={plan.uid} className={`pp-card ${featured ? 'pp-card--featured' : ''}`}>
                      {featured && <div className="pp-card__badge">Most popular</div>}
                      <div className="pp-card__tier">{plan.name}</div>
                      <div className="pp-card__price">
                        <span className="pp-card__currency">$</span>
                        <span className="pp-card__amount">{Number(plan.price).toFixed(0)}</span>
                        <span className="pp-card__period">/{periodLabel(plan.billing_period_days)}</span>
                      </div>
                      {plan.billing_period_days !== 30 && (
                        <div className="pp-card__monthly">
                          ${monthly}/mo
                          {saving > 0 && (
                            <span className="pp-card__save-badge">Save {saving}%</span>
                          )}
                        </div>
                      )}
                      {trial ? (
                        <div className="pp-card__trial">7-day free trial · No credit card required</div>
                      ) : (
                        <div className="pp-card__trial pp-card__trial--empty" />
                      )}
                      <div className="pp-card__divider" />
                      <ul className="pp-card__features">
                        {features.map((f, fi) => (
                          <li key={fi} className="pp-card__feature">
                            <CheckIcon featured={featured} />
                            <span>{f}</span>
                          </li>
                        ))}
                      </ul>
                      <button
                        className="pp-card__btn"
                        onClick={() => handleSelectPlan(plan)}
                        disabled={!!processingPlan || !plan.dodo_price_id}
                      >
                        {busy ? 'Redirecting…' : plan.dodo_price_id ? 'Buy now' : 'Coming Soon'}
                      </button>
                      {trial && plan.dodo_price_id && (
                        <p className="pp-card__note">Cancel anytime. No hidden fees.</p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default UpsellModal;
